import React from 'react'
import styled from 'styled-components'

import { darkBlueFontColor } from 'assets/styles/variables'

interface SentimentGapBarProps {
  focusSentiment: number
  cohortSentiment: number
  focusEntityName: string
  container?: boolean
}

/** Map a sentiment value in [-1, 1] to a percentage position [0, 100]. */
function toPercent(value: number): number {
  return ((value + 1) / 2) * 100
}

function formatSentiment(value: number): string {
  return value.toFixed(2)
}

export const SentimentGapBar: React.FC<SentimentGapBarProps> = ({
  focusSentiment,
  cohortSentiment,
  focusEntityName,
  container = false,
}) => {
  const diff = focusSentiment - cohortSentiment
  const direction = diff < 0 ? 'lower' : 'higher'
  const focusColor = diff >= 0 ? '#059669' : '#DC2626'
  const gapLabel = `${Math.abs(diff).toFixed(2)} ${direction} sentiment`

  return (
    <Container $container={container}>
      <Header>
        <GapLabel>{gapLabel}</GapLabel>
        <GapSubtitle>than cohort average</GapSubtitle>
      </Header>

      <BarWrapper>
        <GradientBar />
        <Dot $pct={toPercent(focusSentiment)} $color={focusColor} />
        <Dot $pct={toPercent(cohortSentiment)} $color="#9ca3af" />
      </BarWrapper>

      <Legend>
        <LegendRow>
          <LegendDot $color={focusColor} />
          <LegendName>{focusEntityName}</LegendName>
          <LegendValue>{formatSentiment(focusSentiment)}</LegendValue>
        </LegendRow>
        <LegendRow>
          <LegendDot $color="#9ca3af" />
          <LegendName>Cohort average</LegendName>
          <LegendValue>{formatSentiment(cohortSentiment)}</LegendValue>
        </LegendRow>
      </Legend>
    </Container>
  )
}

const Container = styled.div<{ $container: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: ${({ $container }) => ($container ? '16px' : '0')};
  border: ${({ $container }) => ($container ? '1px solid #e5e7eb' : '0')};
  border-radius: ${({ $container }) => ($container ? '8px' : '0')};
  width: ${({ $container }) => ($container ? '280px' : '100%')};
  flex-shrink: ${({ $container }) => ($container ? 0 : 1)};
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const GapLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${darkBlueFontColor};
`

const GapSubtitle = styled.span`
  font-size: 12px;
  color: ${darkBlueFontColor};
  opacity: 0.55;
`

const BarWrapper = styled.div`
  position: relative;
  height: 12px;
  margin: 4px 0;
`

const GradientBar = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 6px;
  background: linear-gradient(to right, #fca5a5 0%, #fde68a 50%, #86efac 100%);
`

const Dot = styled.div<{ $pct: number; $color: string }>`
  position: absolute;
  top: 50%;
  left: ${({ $pct }) => $pct}%;
  transform: translate(-50%, -50%);
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`

const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const LegendDot = styled.div<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`

const LegendName = styled.span`
  font-size: 12px;
  color: ${darkBlueFontColor};
  opacity: 0.55;
  flex: 1;
`

const LegendValue = styled.span`
  font-size: 12px;
  color: ${darkBlueFontColor};
  font-weight: 600;
`
